import ReportTable from "../ReportTable.component";
import { useEffect, useMemo, useRef, useState } from "react";
import ReportModal from "../ReportModal.component";
import ReportService from "../../../services/reports/Report.service";
import BaseReport from "../BaseReport.component";
import { MoneyDataItem, PercentDataItem } from "../../../interfaces/reports/BaseReport.interface";
import EpochMonthService from "../../../services/EpochMonth.service";
import MonthSelector from "../../../tailwindComponents/ui/dates/month-selector.component";
import { GlobalAnalyticsParsedData, GlobalAnalyticsRawData } from "../../../interfaces/reports/GlobalAnalyticsReport.interface";

const TODAY_EPOCH = EpochMonthService.getTodayEpochMonth();

interface GlobalAnalyticsReportProps {
    clientId: string | number;
    epochMonth: number | null;
    hierarchyOverride?: Record<string, string[]>;
    fetchDataFn?: (args: { clientId: string | number; startEpochMonth: number; endEpochMonth: number; monthsBack: number }) => Promise<{ data?: any[]; parentLevelData?: any[] } | undefined>;
    parseDataFn?: (raw: GlobalAnalyticsRawData[]) => GlobalAnalyticsParsedData[];
}

const HIERARCHY_OPTIONS: Record<string, string[]> = {
    "By Location": ["locationCode"],
    "Location By Class": ["locationCode", "classCode"],
    "Location by Class by Child": ["locationCode", "parentClientClassCode", "classCode"],
    "Location By Category": ["locationCode", "category"],
    "Location by Category by Class": ["locationCode", "category", "classCode"],
    "By Class": ["classCode"],
    "Class by Child": ["parentClientClassCode", "classCode"],
    "Class by Child by Location": ["parentClientClassCode", "classCode", "locationCode"],
    "Class by Location": ["classCode", "locationCode"],
    "By Category": ["category"],
    "Category by Location": ["category", "locationCode"],
    "Category by Class": ["category", "classCode"],
    "Category by Class by Location": ["category", "classCode", "locationCode"],
    "Category by Class by Child": ["category", "parentClientClassCode", "classCode"],
};

const processRawData = (raw: GlobalAnalyticsRawData[]): GlobalAnalyticsParsedData[] => {
    if (!raw || raw.length === 0) return [];
    return raw.map((r) => ({
        locationCode: r.locationCode,
        locationName: r.locationName,
        classCode: r.classCode,
        className: r.className,
        parentClientClassCode: r.parentClientClassCode ?? null,
        category: r.category ? r.category == "" ? "N/A" : r.category : "N/A",
        categorySortOrder: r.categorySortOrder ?? null,

        lastMonthSalesPlan: new MoneyDataItem(r.salesPlanLm),
        lastMonthSales: new MoneyDataItem(r.salesLm),
        last90MarkdownPct: new PercentDataItem(0), // calc later
        last90InflowRetail: new MoneyDataItem(r.inflowRetailL3),
        last90InflowCost: new MoneyDataItem(r.inflowCostL3),
        last90Sales: new MoneyDataItem(r.salesL3),
        last90SalesVsLastYear: new PercentDataItem(0),
        thisMonthSalesPlan: new MoneyDataItem(r.salesPlan),
        thisMonthInventoryPlan: new MoneyDataItem(r.inventoryBomPlan),
        thisMonthInventory: new MoneyDataItem(r.inventoryBomRm),
        invPctTotal: new PercentDataItem(0),
        salesPctLm: new PercentDataItem(0),
        salesPctLy: new PercentDataItem(0),
        inflowPlanPct: new PercentDataItem(0),
        inflowPlusDeviation: new MoneyDataItem(r.inflowPlusDeviation),
        inflowPlusDeviationCost: new MoneyDataItem(r.inflowPlusDeviationCost),
        onOrderCost: new MoneyDataItem(r.onOrderCost),
        onOrderRetail: new MoneyDataItem(r.onOrderRetail),

        nonNegativeInflowDeviation: new MoneyDataItem(r.inflowPlusDeviation < 0 ? 0 : r.inflowPlusDeviation),
        salesL3Ly: new MoneyDataItem(r.salesL3Ly ?? 0),
        markdownsL3: new MoneyDataItem(r.markdownsL3 ?? 0),
        salesL12: new MoneyDataItem(r.salesL12 ?? 0),
    }));
};

const reapplyPercentCalcs = (data: GlobalAnalyticsParsedData[], pricingType: "Retail" | "Cost") => {
    if (!data || data.length === 0) return data;
    const isCost = pricingType === "Cost";

    return data.map((item) => {
        const mdPct = item.last90Sales?.value && item.markdownsL3?.value
            ? (Number(item.markdownsL3.value) / Number(item.last90Sales.value)) * 100
            : 0;

        return {
            ...item,
            last90MarkdownPct: new PercentDataItem(mdPct),
        } as GlobalAnalyticsParsedData;
    });
};

const GlobalAnalyticsReport = ({ clientId, epochMonth, hierarchyOverride, fetchDataFn, parseDataFn }: GlobalAnalyticsReportProps) => {
    const dataRef = useRef<GlobalAnalyticsRawData[]>([]);
    const parentDataRef = useRef<GlobalAnalyticsRawData[]>([]);
    const [parsedData, setParsedData] = useState<GlobalAnalyticsParsedData[]>([]);
    const [parentParsedData, setParentParsedData] = useState<GlobalAnalyticsParsedData[]>([]);
    const [sortedData, setSortedData] = useState<GlobalAnalyticsParsedData[]>([]);
    const [loading, setLoading] = useState(true);
    const [hierarchy, setHierarchy] = useState<string[] | null>(Object.values(hierarchyOverride ?? {})[0] ?? ["locationCode", "classCode"]);
    const [startEpochMonth, setStartEpochMonth] = useState<number>(epochMonth ?? EpochMonthService.getTodayEpochMonth());
    const [endEpochMonth, setEndEpochMonth] = useState<number>(epochMonth ? epochMonth + 3 : EpochMonthService.getTodayEpochMonth() + 3);
    const [monthsBack, setMonthsBack] = useState<number>(3);
    const [pricingType, setPricingType] = useState<"Retail" | "Cost">("Retail");

    // Filters
    const [locationFilter, setLocationFilter] = useState<string[]>([]);
    const [classFilter, setClassFilter] = useState<string[]>([]);
    const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

    // Only hierarchy columns for now
    let hierarchyColumns: Array<{ name: string; sortable?: boolean; customClassName?: string }> = [];
    for (let i = 0; i < (hierarchy?.length ?? 0); i++) {
        hierarchyColumns.push({
            name: "",
            sortable: false,
            customClassName: i === 0
                ? "px-2 py-2 bg-gray-500 text-white"
                : "px-2 py-2 bg-purple-100 border-purple-700 text-purple-800"
        });
    }

    const columns = [
        ...hierarchyColumns,
        { name: "LM - Sales Plan", sortable: true, customClassName: "px-2 py-2" },
        { name: "LM - Sales", sortable: true, customClassName: "px-2 py-2" },
        { name: "Markdown %", sortable: true, customClassName: "px-2 py-2" },
        { name: "Inflow Over Time", sortable: true, customClassName: "px-2 py-2" },
        { name: "Sales Over Time", sortable: true, customClassName: "px-2 py-2" },
        { name: "Sales Over Time vs LY", sortable: true, customClassName: "px-2 py-2" },
        { name: "Sales Plan", sortable: true, customClassName: "px-2 py-2" },
        { name: "Inventory Plan", sortable: true, customClassName: "px-2 py-2" },
        { name: "Inventory", sortable: true, customClassName: "px-2 py-2" },
        { name: "Inv % of Group", sortable: true, customClassName: "px-2 py-2" },
        { name: "LM Sales % of Gr.", sortable: true, customClassName: "px-2 py-2" },
        { name: "LY Sales % of Gr.", sortable: true, customClassName: "px-2 py-2" },
        { name: "Inflow % of Gr.", sortable: true, customClassName: "px-2 py-2" },
        { name: "Inflow + Deviation", sortable: true, customClassName: "px-2 py-2" },
        { name: "On Order", sortable: true, customClassName: "px-2 py-2" },
    ];

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const args = { clientId, startEpochMonth, endEpochMonth, monthsBack };
                const response = await (fetchDataFn ? fetchDataFn(args) : ReportService.getGlobalAnalyticsReportData(args));
                if (response) {
                    dataRef.current = response.data ?? [];
                    parentDataRef.current = response.parentLevelData ?? [];

                    const childParsed = (parseDataFn ?? processRawData)(response.data ?? []);
                    const parentParsed = (parseDataFn ?? processRawData)(response.parentLevelData ?? []);
                    setParsedData(childParsed);
                    setParentParsedData(parentParsed);
                } else {
                    dataRef.current = [];
                    parentDataRef.current = [];
                    setParsedData([]);
                    setParentParsedData([]);
                    setSortedData([]);
                }
            } catch (e) {
                setParsedData([]);
                setParentParsedData([]);
                setSortedData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [clientId, startEpochMonth, endEpochMonth, monthsBack]);

    useEffect(() => {
        setEndEpochMonth(startEpochMonth + 3);
    }, [startEpochMonth]);

    useEffect(() => {
        const useParent = hierarchy?.includes("parentClientClassCode");
        const source = useParent ? parentParsedData : parsedData;
        if (source.length > 0) {
            const newSorted = ReportService.sortAndFilterData(
                source,
                hierarchy ?? ["locationCode", "classCode"],
                locationFilter,
                classFilter,
                categoryFilter
            );
            // Reapply percent calculations after sorting/filtering
            setSortedData(reapplyPercentCalcs(newSorted, pricingType));
        } else {
            setSortedData([]);
        }
    }, [hierarchy, parsedData, parentParsedData, locationFilter, classFilter, categoryFilter, pricingType]);

    const tableData = useMemo(() => {
        const isCost = pricingType === "Cost";

        // Carrying totals used to compute % to group like ClassPerformance
        let grandTotal = { inventory: 0, salesLm: 0, inflowDev: 0, salesL12: 0, salesL3: 0, salesL3ly: 0 };
        let groupTotal1 = { inventory: 0, salesLm: 0, inflowDev: 0, salesL12: 0, salesL3: 0, salesL3ly: 0 };
        let groupTotal2 = { inventory: 0, salesLm: 0, inflowDev: 0, salesL12: 0, salesL3: 0, salesL3ly: 0 };

        return sortedData.map((item) => {
            const labels = ReportService.hierarchyItemToVerticalDisplayNames(item, hierarchy ?? ["locationCode", "classCode"]);
            const totalCount = labels.filter(label => label.includes("TOTAL")).length;
            const hierarchyLength = hierarchy?.length ?? 2;

            // Update carrying totals based on totalCount (mirrors ClassPerformance logic)
            if (totalCount === hierarchyLength) {
                // Grand total
                grandTotal = {
                    inventory: Number(item.thisMonthInventory?.value) || 0,
                    salesLm: Number(item.lastMonthSales?.value) || 0,
                    inflowDev: Number(item.nonNegativeInflowDeviation?.value) || 0,
                    salesL12: Number(item.salesL12?.value) || 0,
                    salesL3: Number(item.last90Sales?.value) || 0,
                    salesL3ly: Number(item.salesL3Ly?.value) || 0
                };
            } else if (totalCount === hierarchyLength - 1) {
                // Group total 1
                groupTotal1 = {
                    inventory: Number(item.thisMonthInventory?.value) || 0,
                    salesLm: Number(item.lastMonthSales?.value) || 0,
                    inflowDev: Number(item.nonNegativeInflowDeviation?.value) || 0,
                    salesL12: Number(item.salesL12?.value) || 0,
                    salesL3: Number(item.last90Sales?.value) || 0,
                    salesL3ly: Number(item.salesL3Ly?.value) || 0
                };
            } else if (totalCount === hierarchyLength - 2 && hierarchyLength >= 3) {
                // Group total 2 (for 3-level hierarchies)
                groupTotal2 = {
                    inventory: Number(item.thisMonthInventory?.value) || 0,
                    salesLm: Number(item.lastMonthSales?.value) || 0,
                    inflowDev: Number(item.nonNegativeInflowDeviation?.value) || 0,
                    salesL12: Number(item.salesL12?.value) || 0,
                    salesL3: Number(item.last90Sales?.value) || 0,
                    salesL3ly: Number(item.salesL3Ly?.value) || 0
                };
            }

            // Determine which carrying total to use for percent-of-group calculations
            let useTotal = grandTotal;
            if (totalCount === 0) {
                // Leaf rows: choose lowest available carrying total
                useTotal = hierarchyLength === 1 ? grandTotal :
                    hierarchyLength === 2 ? groupTotal1 :
                    (groupTotal2.inventory ? groupTotal2 : groupTotal1);
            } else if (totalCount === hierarchyLength - 1) {
                // This is a group total row - compare against grand total
                useTotal = grandTotal;
            } else if (totalCount === hierarchyLength - 2 && hierarchyLength >= 3) {
                useTotal = groupTotal1;
            }

            const invVal = Number(item.thisMonthInventory?.value) || 0;
            const salesVal = Number(item.lastMonthSales?.value) || 0;
            const last12SalesVal = Number(item.salesL12?.value) || 0;
            const inflowVal = Number(item.nonNegativeInflowDeviation?.value) || 0;
            const salesL3Val = Number(item.last90Sales?.value) || 0;
            const salesL3LyVal = Number(item.salesL3Ly?.value) || 0;

            const invPct = useTotal.inventory ? (invVal / useTotal.inventory) * 100 : 0;
            const salesPctLm = useTotal.salesLm ? (salesVal / useTotal.salesLm) * 100 : 0;
            const salesPctLy = useTotal.salesL12 ? (last12SalesVal / useTotal.salesL12) * 100 : 0;
            const inflowPlanPct = useTotal.inflowDev ? (inflowVal / useTotal.inflowDev) * 100 : 0;
            const salesVsLyPct = salesL3LyVal && salesL3Val ? ((salesL3Val - salesL3LyVal) / salesL3LyVal) * 100 : 0;

            const formatPct = (v: number) => v ? `${v.toFixed(0)}%` : "";

            return [
                ...labels,
                item.lastMonthSalesPlan.formatted,
                item.lastMonthSales.formatted,
                item.last90MarkdownPct ? item.last90MarkdownPct.formatted : "",
                item.last90InflowRetail.formatted,
                item.last90Sales.formatted,
                formatPct(salesVsLyPct),
                item.thisMonthSalesPlan.formatted,
                item.thisMonthInventoryPlan.formatted,
                item.thisMonthInventory.formatted,
                invPct ? formatPct(invPct) : "0%",
                formatPct(salesPctLm),
                formatPct(salesPctLy),
                formatPct(inflowPlanPct),
                isCost ? item.inflowPlusDeviationCost.formatted : item.inflowPlusDeviation.formatted,
                isCost ? item.onOrderCost.formatted : item.onOrderRetail.formatted,
            ];
        });
    }, [sortedData, hierarchy, pricingType]);

    const rowClassName = (rowIndex: number) => {
        if (!tableData || rowIndex < 0 || rowIndex >= tableData.length) return null;
        return ReportService.getRowClassName(tableData[rowIndex], hierarchy ?? ["locationCode", "classCode"]);
    };

    return (
        <div className="w-full h-full flex flex-col items-center justify-center text-center">
            <h2 className="text-2xl font-bold mb-4">{`${EpochMonthService.epochMonthToMMMYY(startEpochMonth ?? EpochMonthService.getTodayEpochMonth() - 12)} to ${EpochMonthService.epochMonthToMMMYY(endEpochMonth ?? EpochMonthService.getTodayEpochMonth())} Global Analytics Beta`}</h2>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <div className="h-full max-w-[1700px]">
                    <BaseReport
                        title={`${EpochMonthService.epochMonthToMMMYY(startEpochMonth ?? EpochMonthService.getTodayEpochMonth() - 12)} to ${EpochMonthService.epochMonthToMMMYY(endEpochMonth ?? EpochMonthService.getTodayEpochMonth())} Global Analytics Beta`}
                        tableColumns={columns.map((c) => c.name)}
                        tableData={tableData}
                        fileName={`Global_Analytics_Beta_${new Date().toLocaleDateString()}`}
                        setHierarchy={setHierarchy}
                        hierarchy={hierarchy}
                        hierarchyOptions={hierarchyOverride ?? HIERARCHY_OPTIONS}
                        parsedData={parsedData}
                        filteredLocationCodes={locationFilter}
                        filteredClassCodes={classFilter}
                        setFilteredLocationCodes={setLocationFilter}
                        setFilteredClassCodes={setClassFilter}
                        filteredCategories={categoryFilter}
                        setFilteredCategories={setCategoryFilter}
                        onApplyFilters={() => {
                            setParsedData((parseDataFn ?? processRawData)(dataRef.current));
                            setParentParsedData((parseDataFn ?? processRawData)(parentDataRef.current));
                        }}
                        hasSecondaryToolbar={true}
                    />
                    <div className="flex flex-row flex-wrap items-center space-x-4 mb-4 ml-8">
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Start Period</span>
                            <MonthSelector epochMonth={startEpochMonth} setEpochMonth={setStartEpochMonth} minMonth={TODAY_EPOCH - 12} maxMonth={TODAY_EPOCH} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">End Period</span>
                            <MonthSelector epochMonth={endEpochMonth} setEpochMonth={setEndEpochMonth} minMonth={startEpochMonth} maxMonth={startEpochMonth + 11} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Months Back</span>
                            <select
                                value={monthsBack}
                                onChange={e => {
                                    const val = parseInt(e.target.value, 10);
                                    setMonthsBack(val);
                                }}
                                className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            >
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                                    <option key={m} value={m}>{m} Months</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Pricing Type</span>
                            <select
                                value={pricingType}
                                onChange={e => {
                                    const val = e.target.value;
                                    setPricingType(val === 'Retail' ? 'Retail' : 'Cost');
                                }}
                                className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            >
                                <option value="Retail">Retail</option>
                                <option value="Cost">Cost</option>
                            </select>
                        </div>
                    </div>

                    <ReportTable
                        columns={columns}
                        data={tableData}
                        rowClassName={rowClassName}
                        defaultSortColumn={null}
                        defaultSortDirection="asc"
                        headerClassName="bg-slate-700 !text-white !text-center py-2 px-1"
                        toPdf={() => (window as any).BaseReportExport && (window as any).BaseReportExport('pdf')}
                        toCsv={() => (window as any).BaseReportExport && (window as any).BaseReportExport('csv')}
                        stickyHeader={true}
                        hierarchyDepth={hierarchy ? hierarchy.length : 0}
                    />
                </div>
            )}
        </div>
    );
};

interface GlobalAnalyticsReportModalProps {
    clientId: string | number;
    epochMonth: number | null;
    onClose: () => void;
}

const GlobalAnalyticsReportModal = ({ clientId, epochMonth, onClose }: GlobalAnalyticsReportModalProps) => {
    return (
        <ReportModal onClose={onClose}>
            <GlobalAnalyticsReport clientId={clientId} epochMonth={epochMonth} />
        </ReportModal>
    );
};

export { GlobalAnalyticsReport, GlobalAnalyticsReportModal };
